import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { UserMinus, Calculator, CheckCircle2, AlertTriangle, Banknote } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { EmptyState, CardSkeleton } from './common/States';
import { api } from '../services/api';
import { getErrorMessage } from '../lib/utils';

const money = (v) => Number(v || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

const STATUS_STYLE = {
  draft: 'bg-slate-500/15 text-slate-600',
  approved: 'bg-blue-500/15 text-blue-700',
  paid: 'bg-emerald-500/15 text-emerald-700',
};

const Line = ({ label, value, negative = false, strong = false }) => (
  <div className={`flex items-center justify-between py-1 text-[13px] ${strong ? 'border-t pt-2 font-medium' : ''}`}>
    <span className={strong ? 'text-foreground' : 'text-muted-foreground'}>{label}</span>
    <span className={`tabular-nums ${negative ? 'text-danger' : 'text-foreground'}`}>
      {negative ? '−' : ''}{money(value)}
    </span>
  </div>
);

const FinalSettlements = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employeeId, setEmployeeId] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/settlements');
      setRows(data.settlements || []);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not load settlements'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runPreview = async () => {
    if (!employeeId.trim()) { toast.error('An employee id is needed'); return; }
    setBusy('preview');
    try {
      const { data } = await api.get(`/api/settlements/preview/${employeeId.trim()}`);
      setPreview(data);
    } catch (e) {
      setPreview(null);
      toast.error(getErrorMessage(e, 'Could not compute a settlement'));
    } finally {
      setBusy('');
    }
  };

  const create = async () => {
    setBusy('create');
    try {
      await api.post('/api/settlements', { employee_id: preview.employee_id });
      toast.success('Settlement created as a draft', {
        description: 'It needs a second person to approve it before payment.',
      });
      setPreview(null);
      setEmployeeId('');
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not create the settlement'));
    } finally {
      setBusy('');
    }
  };

  const approve = async (row) => {
    setBusy(`approve-${row.id}`);
    try {
      await api.post(`/api/settlements/${row.id}/approve`);
      toast.success('Settlement approved');
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not approve it'));
    } finally {
      setBusy('');
    }
  };

  const markPaid = async (row) => {
    const reference = window.prompt('Payment reference:');
    if (reference === null) return;
    setBusy(`pay-${row.id}`);
    try {
      const { data } = await api.post(`/api/settlements/${row.id}/mark-paid`, { reference });
      toast.success('Settlement paid', {
        description: data.loans_closed
          ? `${data.loans_closed} loan(s) closed by the recovery`
          : undefined,
      });
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not mark it paid'));
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold tracking-tight">Full &amp; final settlement</h2>
        <p className="mt-1 max-w-3xl text-[13px] text-muted-foreground">
          A leaver&apos;s last payment: gratuity, unused leave, and what they owe
          back. Separate from the monthly payslip, prepared by one person and
          approved by another — and it can come out negative.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Calculator className="h-4 w-4" />Compute a settlement
          </CardTitle>
          <p className="text-[13px] text-muted-foreground">
            Needs a recorded last working day — that is what tells the calculation
            which period it covers.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label className="text-xs text-muted-foreground">Employee ID</Label>
              <Input className="mt-1 w-48" value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)} />
            </div>
            <Button onClick={runPreview} disabled={busy === 'preview'}>
              {busy === 'preview' ? 'Computing…' : 'Preview'}
            </Button>
          </div>

          {preview && (
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-lg border border-border p-4">
                <p className="mb-2 text-[11px] uppercase tracking-widest text-muted-foreground">
                  Owed to {preview.employee_id}
                </p>
                <Line label="Unpaid salary" value={preview.unpaid_salary} />
                <Line
                  label={`Leave encashment (${preview.leave_encashment_days} day(s))`}
                  value={preview.leave_encashment}
                />
                <Line label="Gratuity" value={preview.gratuity} />
                <Line label="Total earnings" value={preview.total_earnings} strong />
                {!preview.gratuity_eligible && (
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    No gratuity: {preview.service_years} years of service, and five
                    are needed.
                  </p>
                )}
              </div>

              <div className="rounded-lg border border-border p-4">
                <p className="mb-2 text-[11px] uppercase tracking-widest text-muted-foreground">
                  Recovered
                </p>
                <Line
                  label={`Notice shortfall (${preview.notice_shortfall_days} day(s))`}
                  value={preview.notice_recovery} negative
                />
                <Line label="Outstanding loans" value={preview.loan_recovery} negative />
                <Line label="Total recoveries" value={preview.total_recoveries} negative strong />
              </div>

              <div className="md:col-span-2">
                {preview.employee_owes_company ? (
                  <div className="flex gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-[13px] text-amber-800">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <div>
                      <b>This settlement is negative — {preview.employee_id} owes
                      {' '}{money(Math.abs(preview.net_payable))}.</b> Somebody has to
                      collect it; it will not come out of a future payslip.
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 text-[13px] text-emerald-800">
                    <span>Net payable</span>
                    <span className="text-base font-semibold tabular-nums">
                      {money(preview.net_payable)}
                    </span>
                  </div>
                )}
                <Button className="mt-3" onClick={create} disabled={busy === 'create'}>
                  Create settlement
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Settlements</CardTitle></CardHeader>
        <CardContent className="p-0">
          {loading ? <CardSkeleton rows={5} className="border-0" /> : rows.length === 0 ? (
            <EmptyState icon={UserMinus} title="No settlements yet"
              description="Compute one for an employee who has a recorded last working day." />
          ) : (
            <Table className="text-hr-sm">
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Last working day</TableHead>
                  <TableHead className="text-right">Service</TableHead>
                  <TableHead className="text-right">Earnings</TableHead>
                  <TableHead className="text-right">Recoveries</TableHead>
                  <TableHead className="text-right">Net</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.employee_id}</TableCell>
                    <TableCell>{r.last_working_date}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {Number(r.service_years).toFixed(1)} yr
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{money(r.total_earnings)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(r.total_recoveries)}</TableCell>
                    <TableCell className={`text-right font-medium tabular-nums ${r.employee_owes_company ? 'text-danger' : ''}`}>
                      {money(r.net_payable)}
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_STYLE[r.status] || ''}>
                        {r.status === 'paid' && <CheckCircle2 className="mr-1 inline h-3 w-3" />}
                        {r.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right">
                      {r.status === 'draft' && (
                        <Button size="sm" variant="outline" disabled={busy === `approve-${r.id}`}
                          onClick={() => approve(r)}>Approve</Button>
                      )}
                      {r.status === 'approved' && (
                        <Button size="sm" variant="outline" disabled={busy === `pay-${r.id}`}
                          onClick={() => markPaid(r)}>
                          <Banknote className="mr-1 h-3 w-3" />Mark paid
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {rows.length > 0 && (
            <p className="border-t p-3 text-xs text-muted-foreground">
              A settlement is approved by someone other than whoever prepared it.
              Marking it paid closes any loans the recovery cleared.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FinalSettlements;
